import ApiOffers, {defaultParams} from "@/api/methods/ApiOffers";
import {UserDataHelper} from "@/api/helpers";
import {PRIVILEGES} from "@/layout/Constants";
import { clientTz } from "@/plugins/timezone/timezone.helper";

export default {
    namespaced: true,
    state: {
        allNotes: null,
        selectedNote: null,
        offersList: [],
        selectedOfferType: defaultParams.offerStatus,
        sellerParams: {
            defaultContingencyPeriod: 7,
            minContingencyPeriod: 1,
            maxContingencyPeriod: 30,
            daysForCloseEscrow: 30,
            daysForOffersShowing: 30
        },
        offersCount: {
            active: 0,
            inEscrow: 0,
            completed: 0,
            cancelled: 0,
            expired: 0,
            withdrawn: 0,
            rejected: 0,
            boughtByOther: 0,
            all: 0
        },
        hasContingencyPrivilege: false,
        ignoreOfferCreationDate: false,
        addressForSearch: "",
        storeNoteIdForSearch: "",
        sortByField: null,
        sortByDirection: "desc",
    },
    actions: {
        setDefaults({commit}) {
            commit("setSelectedNote", null);
            commit("setAllNotes", null);
            commit("setOffersList", []);
            commit("setSelectedOfferType", defaultParams.offerStatus);
            commit("setIgnoreOfferCreationDate", false);
            commit("setAddressForSearch", "");
            commit("setStoreNoteIdForSearch", "");
            commit("setSortByField", null);
            commit("setSortByDirection", "desc");
        },
        async getOffersByTimer({commit, dispatch, state}, params) {
            let user = UserDataHelper.getUser();
            let hasContingencyPrivilege = UserDataHelper.isPrivilegeExists(user, PRIVILEGES.SHOW_CONTINGENCY_IN_OFFER);
            commit("setHasContingencyPrivilege", hasContingencyPrivilege);

            const lastOffersOrderNumbers = state.offersList.reduce((acc, offer) => {
                const isLastOffer = offer.buyer != null;
                if (isLastOffer) {
                    let offersHistoryByOrderNumber = state.offersList.filter((curOffer) => curOffer.orderNumber == offer.orderNumber && curOffer.buyer == null);
                    if (offersHistoryByOrderNumber && offersHistoryByOrderNumber.length > 0 && !acc.includes(offer.orderNumber)) {
                        acc.push(offer.orderNumber);
                    }
                }
                return acc;
            }, []);
            params.needPhoto = false;
            params.needStatusScale = false;
            params.timezone = clientTz();
            params.orderNumbersWithAllOffers = lastOffersOrderNumbers;
            if (params.noteId && state.allNotes && state.allNotes.length > 0 && params.noteId == state.allNotes[0].noteId) {
                delete params.noteId;
            }
            commit("setSelectedOfferType", params.offerStatus);
            params.ignoreOfferCreationDate = state.ignoreOfferCreationDate;
            params.address = state.addressForSearch;
            params.storeNoteId = state.storeNoteIdForSearch;
            params.sortByField = state.sortByField;
            params.sortByDirection = state.sortByDirection;

            return await ApiOffers.getOffersForSeller(params)
                .then((response) => {
                    if (!response.data) {
                        return;
                    }

                    const sellerParams = {
                        defaultContingencyPeriod: response.data.defaultContingencyPeriod,
                        minContingencyPeriod: response.data.minContingencyPeriod,
                        maxContingencyPeriod: response.data.maxContingencyPeriod,
                        daysForCloseEscrow: response.data.daysForCloseEscrow,
                        daysForOffersShowing: response.data.daysForOffersShowing
                    };
                    commit("setSellerParams", sellerParams);

                    const offersCount = {
                        active: response.data.activeOffersCount,
                        inEscrow: response.data.escrowOffersCount,
                        completed: response.data.completedOffersCount,
                        cancelled: response.data.cancelledOffersCount,
                        expired: response.data.expiredOffersCount,
                        withdrawn: response.data.withdrawnOffersCount,
                        rejected: response.data.rejectedOffersCount,
                        boughtByOther: response.data.boughtByOthersOffersCount,
                        all: response.data.allOffersCount
                    };
                    commit("setOffersCount", offersCount);

                    if (response.data.notes && response.data.notes.length == 1 &&
                        (!response.data.notes[0].offersList || (response.data.notes[0].offersList && response.data.notes[0].offersList.length == 0))) {
                        commit("setAllNotes", []);
                    } else {
                        commit("setAllNotes", response.data.notes);

                        let newSelectedNoteData;
                        if (params.noteId) {
                            newSelectedNoteData = response.data.notes.find((note) => note.noteId === params.noteId);
                            if (newSelectedNoteData) {
                                newSelectedNoteData.offersList.forEach((offer) => {
                                    if (lastOffersOrderNumbers.indexOf(offer.orderNumber) != -1) {
                                        offer.isLastOffer = true;
                                        offer.lastOffersLoaded = true;
                                    }
                                });
                                commit("setOffersList", newSelectedNoteData.offersList);
                            } else if (response.data.notes && response.data.notes.length > 0) {
                                const firstNote = response.data.notes[0];
                                if (!firstNote) {
                                    return;
                                }
                                dispatch("getSelectedNote", firstNote.noteId)
                                    .then((note) => {
                                        commit("setSelectedNote", note);
                                        note.offersList.forEach((offer) => {
                                            if (lastOffersOrderNumbers.indexOf(offer.orderNumber) != -1) {
                                                offer.isLastOffer = true;
                                                offer.lastOffersLoaded = true;
                                            }
                                        });
                                        commit("setOffersList", firstNote.offersList);
                                    });
                            }
                        } else if (response.data.notes && response.data.notes.length > 0) {
                            const firstNote = response.data.notes[0];
                            if (!firstNote) {
                                return;
                            }
                            dispatch("getSelectedNote", firstNote.noteId)
                                .then((note) => {
                                    commit("setSelectedNote", note);
                                    firstNote.offersList.forEach((offer) => {
                                        if (lastOffersOrderNumbers.indexOf(offer.orderNumber) != -1) {
                                            offer.isLastOffer = true;
                                            offer.lastOffersLoaded = true;
                                        }
                                    });
                                    commit("setOffersList", firstNote.offersList);
                                });
                        }
                    }
                });
        },
        async getOffersByNote({commit, dispatch, state}, params) {
            let newParams = {
                noteId: params.noteId,
                offerStatus: state.selectedOfferType,
                ignoreOfferCreationDate: state.ignoreOfferCreationDate
            };
            const requestParams = Object.assign(defaultParams, newParams);
            requestParams.timezone = clientTz();
            let callCount = 0;
            return await ApiOffers.getOffersForSellerByNote(requestParams)
                .then((response) => {
                    if (!response.data) {
                        return;
                    }
                    if (response.data && response.data.length > 0) {

                        dispatch("getSelectedNote", params.noteId)
                            .then((note) => {
                                commit("setSelectedNote", note);
                                commit("setOffersList", response.data);
                            });
                    } else {
                        if (callCount == 0) {
                            let params = {
                                noteId: null,
                                offerStatus: state.selectedOfferType
                            };
                            dispatch("getOffersByTimer", params)
                                .then((note) => {
                                    callCount++;
                                });
                        }
                    }
                });
        },
        async getOffersHistory({commit, state}, params) {
            const lastOffersOrderNumbers = state.offersList.reduce((acc, offer) => {
                const isLastOffer = offer.buyer != null;
                if (isLastOffer) {
                    let offersHistoryByOrderNumber = state.offersList.filter((curOffer) => curOffer.orderNumber == offer.orderNumber && curOffer.buyer == null);
                    if (offersHistoryByOrderNumber && offersHistoryByOrderNumber.length > 0 && !acc.includes(offer.orderNumber)) {
                        acc.push(offer.orderNumber);
                    }
                }
                return acc;
            }, []);
            params.timezone = clientTz();
            params.ignoreOfferCreationDate = state.ignoreOfferCreationDate;
            return await ApiOffers.getOffersHistoryForSeller(params)
                .then((response) => {
                    if (!response.data) return;
                    const lastOffers = response.data.map((offer) => {
                        offer.isLastOffer = true;
                        return offer;
                    });
                    const allOffers = state.offersList.reduce((acc, offer) => {
                        acc.push(offer);
                        if (offer.offerId === params.offerId) {
                            lastOffers.forEach((offer) => acc.push(offer));
                        }
                        if (lastOffersOrderNumbers.indexOf(offer.orderNumber) != -1) {
                            offer.lastOffersLoaded = true;
                        }
                        return acc;
                    }, []);
                    commit("setOffersList", allOffers);
                });
        },
        getSelectedNote({state}, noteId) {
            const selectedNote = {...state.allNotes.find((note) => note.noteId === noteId)};
            delete selectedNote.offersList;
            return selectedNote;
        },
        setSelectedNote({commit, state}, note) {
            commit("setSelectedNote", note);
        },
        setIgnoreOfferCreationDate({commit, state}, ignoreOfferCreationDate) {
            commit("setIgnoreOfferCreationDate", ignoreOfferCreationDate);
        },
        setAddressForSearch({commit, state}, addressForSearch) {
            commit("setAddressForSearch", addressForSearch);
        },
        setStoreNoteIdForSearch({commit, state}, storeNoteIdForSearch) {
            commit("setStoreNoteIdForSearch", storeNoteIdForSearch);
        },
        setSortByField({commit, state}, sortByField) {
            commit("setSortByField", sortByField);
        },
        setSortByDirection({commit, state}, sortByDirection) {
            commit("setSortByDirection", sortByDirection);
        }
    },
    mutations: {
        setAllNotes(state, allNotes) {
            state.allNotes = allNotes;
        },
        setSelectedNote(state, note) {
            state.selectedNote = note;
        },
        setOffersList(state, list) {
            if (list == null) {
                state.offersList = [];
            } else {
                state.offersList = list;
            }
        },
        setSelectedOfferType(state, offerType) {
            state.selectedOfferType = offerType;
        },
        setSellerParams(state, sellerParams) {
            state.sellerParams = sellerParams;
        },
        setOffersCount(state, offersCount) {
            state.offersCount = offersCount;
        },
        setHasContingencyPrivilege(state, hasContingencyPrivilege) {
            state.hasContingencyPrivilege = hasContingencyPrivilege;
        },
        setIgnoreOfferCreationDate(state, ignoreOfferCreationDate) {
            state.ignoreOfferCreationDate = ignoreOfferCreationDate;
        },
        setAddressForSearch(state, addressForSearch) {
            state.addressForSearch = addressForSearch;
        },
        setStoreNoteIdForSearch(state, storeNoteIdForSearch) {
            state.storeNoteIdForSearch = storeNoteIdForSearch;
        },
        setSortByField(state, sortByField) {
            state.sortByField = sortByField;
        },
        setSortByDirection(state, sortByDirection) {
            state.sortByDirection = sortByDirection;
        },
    }
};